import {
  ArrowForwardRounded,
  AutoStoriesRounded,
  CategoryRounded,
  DescriptionRounded,
  LibraryBooksRounded,
  PeopleRounded,
  QuizRounded,
  SchoolRounded,
} from "@mui/icons-material";
import { alpha, Box, Card, CardActionArea, Container, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useUserStore } from "../store/user-store";
import { isAdmin, isUser } from "../utils/access-control";
import { ViewModelPageHome } from "../viewmodel/page-home";
import { brand } from "../theme/theme";

type Tile = {
  title: string;
  description: string;
  path: string;
  icon: ReactNode;
  color: string;
  adminOnly?: boolean;
  resetState?: boolean;
};

const tiles: Tile[] = [
  { title: "Temas", description: "Gestão dos temas do quiz", path: "/temas", icon: <CategoryRounded />, color: "#6157E0" },
  { title: "Perguntas", description: "Gerencie as perguntas do quiz", path: "/perguntas", icon: <QuizRounded />, color: "#EC4899", resetState: true },
  { title: "Utilizadores", description: "Crie utilizadores e associe-os a temas", path: "/utilizadores", icon: <PeopleRounded />, color: "#8B5CF6", adminOnly: true },
  { title: "Disciplinas", description: "Livros por disciplina", path: "/livrarias", icon: <LibraryBooksRounded />, color: "#10B981", adminOnly: true },
  { title: "Livros", description: "Lista de todos os livros e auxiliares", path: "/livros", icon: <AutoStoriesRounded />, color: "#0EA5E9", adminOnly: true, resetState: true },
  { title: "Secções", description: "Nível de exames", path: "/schools", icon: <SchoolRounded />, color: "#F59E0B", adminOnly: true },
  { title: "Exames", description: "Gerencie os exames", path: "/exames", icon: <DescriptionRounded />, color: "#EF4444", adminOnly: true, resetState: true },
];

function HomePage() {
  const { navigate, handleResetState } = ViewModelPageHome();
  const { user } = useUserStore();

  const canSee = isAdmin(user) || isUser(user);
  const visible = tiles.filter((t) => canSee && (!t.adminOnly || isAdmin(user)));
  const firstName = user?.name?.split(" ")[0];

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 5,
          color: "#fff",
          p: { xs: 3, md: 5 },
          mb: 4,
          background: brand.gradient,
          boxShadow: "0 24px 50px -24px rgba(97,87,224,0.7)",
          "&::after": {
            content: '""',
            position: "absolute",
            width: 320,
            height: 320,
            right: -80,
            top: -120,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.12)",
          },
        }}
      >
        <Typography variant="body2" sx={{ opacity: 0.85, mb: 1, fontWeight: 600 }}>
          Painel de controlo
        </Typography>
        <Typography variant="h3" component="h1" sx={{ fontSize: { xs: "1.9rem", md: "2.6rem" } }}>
          Olá, {firstName} 👋
        </Typography>
        <Typography sx={{ mt: 1, opacity: 0.9, maxWidth: 520 }}>
          Plataforma de gestão de quiz, perguntas e exames.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
        }}
      >
        {visible.map((t) => (
          <Card
            key={t.path}
            sx={{
              transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                borderColor: alpha(t.color, 0.5),
                boxShadow: `0 20px 40px -20px ${alpha(t.color, 0.55)}`,
                "& .arrow": { transform: "translateX(4px)", color: t.color },
              },
            }}
          >
            <CardActionArea
              sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start" }}
              onClick={() => {
                t.resetState && handleResetState();
                navigate(t.path);
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "14px",
                  display: "grid",
                  placeItems: "center",
                  color: t.color,
                  bgcolor: alpha(t.color, 0.12),
                  mb: 2.5,
                  "& svg": { fontSize: 28 },
                }}
              >
                {t.icon}
              </Box>
              <Typography variant="h6" component="h2">
                {t.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3, flex: 1 }}>
                {t.description}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary", fontSize: 14, fontWeight: 600 }}>
                Abrir
                <ArrowForwardRounded className="arrow" sx={{ fontSize: 18, transition: "all .2s ease" }} />
              </Box>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 6 }}>
        © {new Date().getFullYear()} Aprende+ · Plataforma de Educação Moçambicana
      </Typography>
    </Container>
  );
}

export default HomePage;
